import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useMeasure } from '@/lib/hooks/use-measure';
import { Pager } from './pager';

interface TabsProps {
  tabs: string[];
  contents: React.ReactNode[] | any;
  onTabChange: (index: number) => void;
}

export default function Tabs({ tabs, contents, onTabChange }: Readonly<TabsProps>) {
  const [value, setValue] = useState(0);
  const childRefs = useRef(new Map());
  const tabListRef = useRef();
  const [slider, setSlider] = useState({ left: 0, right: 0, hasValue: false });
  const { bounds, ref } = useMeasure();

  useEffect(() => {
    const target = childRefs.current.get(value);
    const container = tabListRef.current;
    if (!target || !container) return;

    // Use rAF to batch the DOM read with the browser's next paint frame,
    // avoiding a forced reflow between style invalidation and layout query.
    requestAnimationFrame(() => {
      // @ts-ignore
      const cRect = container.getBoundingClientRect();
      if (cRect.width === 0) return;

      const tRect = target.getBoundingClientRect();

      setSlider({
        hasValue: true,
        left: tRect.left - cRect.left + 8,
        right: cRect.right - tRect.right + 8,
      });
    });
  }, [value, bounds]);

  return (
    <div>
      <div ref={ref} className="overflow-hidden shadow-none">
        {/* @ts-ignore */}
        <div ref={tabListRef} className="relative border-b border-gray-300">
          {tabs.map((tab, i) => (
            <motion.button
              key={tab}
              className={`whitespace-nowrap appearance-none box-border text-center capitalize antialiased text-ellipsis leading-6 text-lg font-bold hover:bg-[#f1f3f5]
                ${i === value ? 'text-accent' : 'text-gray-400'} 
                px-4 py-2 cursor-pointer`}
              transition={{ duration: 0.1 }}
              ref={(el) => childRefs.current.set(i, el)}
              onClick={() => {
                setValue(i);
                onTabChange(i);
              }}
            >
              {tab}
            </motion.button>
          ))}

          {slider.hasValue && (
            <motion.div
              className="absolute h-1 bg-accent rounded-tl-lg rounded-tr-lg"
              initial={false}
              style={{
                left: slider.left,
                right: slider.right,
              }}
            />
          )}
        </div>
      </div>
      <Pager value={value}>
        {/* @ts-ignore */}
        {contents.map((content, i) => (
          <div key={i} className="w-full p-4">
            {content}
          </div>
        ))}
      </Pager>
    </div>
  );
}
